import { getMetaTags, mediaUrlFallback, getSSRRequestFullUrl, getVerboliaScriptSrc } from '@/helpers/utils';
import useBreadcrumbsStructuredData from '~/hooks/useBreadcrumbsStructuredData';
import { computed } from '@nuxtjs/composition-api';
import useStructuredData from '~/hooks/useStructuredData';

export const useProductListingHead = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  essentialsResult: any,
  breadcrumbs: any[],
  ssrContext: { req: { headers?: { host?: string }; url?: string } },
  $i18n: { locale: string; defaultLocale?: string },
  translate: (key: string, params?: Record<string, string>) => string,
  hasTranslation: (code: string) => boolean,
  urlInfo: { meta_title?: string; meta_description?: string }
): Record<string, any> => {
  if (!essentialsResult) return {};

  const url = process.server
    ? getSSRRequestFullUrl(ssrContext.req)
    : window.location.href;

  const canonicalUrl = url.split('?')[0];
  const verboliaScriptSrc = getVerboliaScriptSrc(url, $i18n);

  const logoImage =
    essentialsResult.headless_containers?.[0]?.elements?.[0]?.media?.[0]?.media_path || '';

  const hrefLangTags =
    essentialsResult.cmswebsiteredirects?.map((redirect: any) => ({
      hid: `hreflang-${redirect.locale}`,
      rel: 'alternate',
      hreflang: redirect.locale,
      href: redirect.url
    })) || [];

  const fallbackTitle = hasTranslation('srp_meta_title')
    ? translate('srp_meta_title')
    : '';

  const pageTitle =
    urlInfo?.meta_title || essentialsResult.description || fallbackTitle;

  const breadcrumbsStructuredData = useBreadcrumbsStructuredData(breadcrumbs, ssrContext);

  const fallbackDescription = hasTranslation('srp_meta_description')
    ? translate('srp_meta_description')
    : '';

  const containers = computed(() => {
    return JSON.parse(JSON.stringify(essentialsResult.headless_containers || []));
  });

  const { structuredData } = useStructuredData(containers);

  return {
    title: pageTitle,
    meta: getMetaTags({
      title: pageTitle,
      description: urlInfo?.meta_description || essentialsResult.meta_description || fallbackDescription,
      image: mediaUrlFallback(logoImage),
      type: 'product.group',
      locale: $i18n.locale,
      url: canonicalUrl
    }),
    link: [
      { rel: 'canonical', href: canonicalUrl },
      {
        rel: 'preload',
        as: 'script',
        href: 'https://app.aiden.cx/webshop/build/aiden-embedded.min.js',
        crossorigin: true
      },
      ...hrefLangTags
    ],
    script: [
      {
        type: 'text/javascript',
        src: 'https://app.aiden.cx/webshop/build/aiden-embedded.min.js',
        async: true,
        defer: true
      },
      ...(verboliaScriptSrc
        ? [{
          hid: 'verbolia-script',
          type: 'text/javascript',
          src: verboliaScriptSrc,
          async: true
        }]
        : []),
      {
        hid: 'org-ldjson',
        type: 'application/ld+json',
        json: structuredData.value
      },
      {
        hid: 'breadcrumbs-ldjson',
        type: 'application/ld+json',
        json: breadcrumbsStructuredData
      }
    ]
  };
};
